import React, { useState, useEffect } from 'react';
import EmptyState from '../components/EmptyState';
import { useInvoicesData, useClientsData, useQuotesData } from '../hooks/useData';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarDaysIcon,
  PlusIcon,
  TrashIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { Search as SearchIcon } from 'lucide-react';
import { InvoiceActionsMenu } from '../components/Invoices/InvoiceActionsMenu';
import { CreateInvoiceFromJobModal } from '../components/Invoices/CreateInvoiceFromJobModal';

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

type InvoiceItem = {
  id: number;
  description: string;
  quantity: string;
  unitPrice: string;
};

type InvoiceComposerProps = {
  onCancel: () => void;
};

const formatMoney = (value: number) => {
  if (!Number.isFinite(value)) return '£0.00';
  return `£${value.toFixed(2)}`;
};

function InvoiceComposer({ onCancel }: InvoiceComposerProps) {
  const { data: clients = [] } = useClientsData();
  const { data: quotes = [] } = useQuotesData();
  const [showPreview, setShowPreview] = useState(true);
  const [invoiceNumber] = useState('DRAFT-0001');
  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [status, setStatus] = useState('Unpaid');
  const [dueDate, setDueDate] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [items, setItems] = useState<InvoiceItem[]>([
    { id: 1, description: '', quantity: '1', unitPrice: '0' },
  ]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [note, setNote] = useState('');
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const subtotal = items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity || '0') || 0;
    const price = parseFloat(item.unitPrice || '0') || 0;
    return sum + qty * price;
  }, 0);

  const discountAmount = subtotal * (discountPercent / 100);
  const taxedBase = subtotal - discountAmount;
  const taxAmount = taxedBase * (taxPercent / 100);
  const total = taxedBase + taxAmount;

  const handleItemChange = (id: number, field: keyof InvoiceItem, value: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddItem = () => {
    setItems((prev) => {
      const nextId = prev.length > 0 ? Math.max(...prev.map((i) => i.id)) + 1 : 1;
      return [
        ...prev,
        { id: nextId, description: '', quantity: '1', unitPrice: '0' },
      ];
    });
  };

  const handleRemoveItem = (id: number) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((item) => item.id !== id) : prev));
  };

  const handleImportFromQuote = (quoteId: string) => {
    setSelectedQuoteId(quoteId);
    if (!quoteId) return;

    const quote: any = (quotes as any[]).find((q) => q.id === quoteId);
    if (!quote) return;

    if (quote.client?.name) {
      setClientName(quote.client.name);
    }

    const clientFromList = (clients as any[]).find(
      (c) => c.id === (quote.clientId || quote.client?.id)
    );
    if (clientFromList && clientFromList.address) {
      setClientAddress(clientFromList.address);
    }

    if (Array.isArray(quote.items) && quote.items.length > 0) {
      const mappedItems: InvoiceItem[] = quote.items.map((item: any, index: number) => ({
        id: index + 1,
        description: item.description || '',
        quantity: String(item.quantity ?? 1),
        unitPrice: String(item.unitPrice ?? item.totalPrice ?? 0),
      }));
      setItems(mappedItems);
    }
  };

  const handleSaveDraft = () => {
    const payload = {
      invoiceNumber,
      clientName,
      clientAddress,
      status,
      dueDate,
      currency,
      items,
      discountPercent,
      taxPercent,
      note,
      totals: { subtotal, discountAmount, taxAmount, total },
    };
    console.log('Saving invoice draft:', payload);
  };

  const handleSubmit = () => {
    const payload = {
      invoiceNumber,
      clientName,
      clientAddress,
      status,
      dueDate,
      currency,
      items,
      discountPercent,
      taxPercent,
      note,
      totals: { subtotal, discountAmount, taxAmount, total },
    };
    console.log('Submitting invoice:', payload);
    onCancel();
  };

  return (
    <div className="py-6">
      <div className="mb-4 border-b border-[#E6E6E4] pb-3">
        <div className="flex items-center gap-2 text-xs text-[#515959] mb-4">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#D4D4D4] bg-white text-[#4B5563] hover:bg-[#F3F4F6]"
            onClick={onCancel}
          >
            <ChevronLeftIcon className="h-3 w-3" />
          </button>
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#9CA3AF] cursor-default"
            disabled
          >
            <ChevronRightIcon className="h-3 w-3" />
          </button>
          <span className="ml-2 text-[11px] text-[#6B7280]">
            Invoices <span className="mx-1 text-[#D1D5DB]">/</span> Create invoice
          </span>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Create invoice</h1>
            <p className="mt-1 text-xs font-normal text-[#515959]">
              Build a new invoice, review a live preview, and get it ready to send to your client.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="flex items-center gap-2 text-xs text-gray-600"
              onClick={() => setShowPreview((value) => !value)}
            >
              <span className="text-[11px]">Show preview</span>
              <span
                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                  showPreview ? 'bg-orange-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform ${
                    showPreview ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </span>
            </button>

            <button
              type="button"
              onClick={handleSaveDraft}
              className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-white px-3 py-2 text-xs font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
            >
              Save draft
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center justify-center rounded-lg bg-orange-500 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-orange-600 transition-colors"
            >
              Continue to send
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-lg border border-transparent px-3 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-900 tracking-[-0.03em]">Details</h2>
              <span className="text-xs text-gray-400">{invoiceNumber}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Set who you are billing and what you are billing them for.
            </p>

            <div className="mt-4 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Client</label>
                <div className="relative">
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setClientName(value);
                    }}
                    placeholder="Search or enter client name"
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  {clientName && (
                    <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                      {((clients as any[]) || [])
                        .filter((client: any) =>
                          client.name?.toLowerCase().includes(clientName.toLowerCase())
                        )
                        .slice(0, 6)
                        .map((client: any) => (
                          <button
                            key={client.id}
                            type="button"
                            className="flex w-full items-start justify-between px-3 py-2 text-left text-xs hover:bg-gray-50"
                            onClick={() => {
                              setClientName(client.name || '');
                              if (client.address) {
                                setClientAddress(client.address);
                              }
                            }}
                          >
                            <span className="font-medium text-gray-900">{client.name}</span>
                            {client.address && (
                              <span className="ml-2 truncate text-[11px] text-gray-500 max-w-[160px]">
                                {client.address}
                              </span>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Address</label>
                <textarea
                  rows={2}
                  value={clientAddress}
                  onChange={(e) => setClientAddress(e.target.value)}
                  placeholder="Customer billing address"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="Unpaid">Unpaid</option>
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Due date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Import from quote</label>
                  <select
                    value={selectedQuoteId}
                    onChange={(e) => handleImportFromQuote(e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select quote...</option>
                    {(quotes as any[]).map((quote: any) => (
                      <option key={quote.id} value={quote.id}>
                        {quote.quoteNumber} — {quote.jobTitle}
                      </option>
                    ))}
                  </select>
                </div>

                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[minmax(0,2fr)_minmax(0,0.7fr)_minmax(0,0.9fr)_auto] gap-2 items-center"
                  >
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">
                        {index === 0 ? 'Item' : 'Additional item'}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                        placeholder="Description"
                        className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Qty</label>
                      <input
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                        className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Price</label>
                      <input
                        type="number"
                        min="0"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)}
                        className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveItem(item.id)}
                      className="mt-5 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Add item</span>
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 border-t border-gray-100 pt-4 mt-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Discount %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(Number(e.target.value) || 0)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Tax %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(Number(e.target.value) || 0)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Note</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional note on invoice"
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <PaperClipIcon className="h-4 w-4 text-gray-400" />
                  <span>Attachment</span>
                  {attachment && (
                    <span className="ml-2 truncate max-w-[160px] text-[11px] text-gray-500">
                      {attachment.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setAttachment(file);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs font-medium text-orange-600 hover:text-orange-700"
                  >
                    Add file
                  </button>
                </div>
              </div>
            </div>
          </div>

          {showPreview && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-400">Invoice preview</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">{invoiceNumber}</p>
              </div>
              <span className="text-xs font-medium text-orange-600 uppercase tracking-[0.12em]">
                {status}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-xs text-gray-700">
              <div>
                <p className="text-[11px] text-gray-400">Billed to</p>
                <p className="mt-1 font-medium text-gray-900 truncate">{clientName || 'Client name'}</p>
                <p className="mt-1 whitespace-pre-line text-[11px] text-gray-500">
                  {clientAddress || 'Billing address'}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400">Due date</p>
                <p className="mt-1 text-[11px] text-gray-700">
                  {dueDate || 'Not set'}
                </p>
                <p className="mt-3 text-[11px] text-gray-400">Currency</p>
                <p className="mt-1 text-[11px] text-gray-700">{currency}</p>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-gray-200">
              <div className="grid grid-cols-4 gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-medium text-gray-500">
                <span>Description</span>
                <span className="text-center">Qty</span>
                <span className="text-right">Unit price</span>
                <span className="text-right">Amount</span>
              </div>
              <div className="divide-y divide-gray-100">
                {items.map((item) => {
                  const qty = parseFloat(item.quantity || '0') || 0;
                  const price = parseFloat(item.unitPrice || '0') || 0;
                  const lineTotal = qty * price;
                  return (
                    <div key={item.id} className="grid grid-cols-4 gap-2 px-3 py-2 text-[11px] text-gray-700">
                      <span className="truncate">{item.description || 'Item'}</span>
                      <span className="text-center">{qty || ''}</span>
                      <span className="text-right">{lineTotal ? formatMoney(price) : ''}</span>
                      <span className="text-right">{lineTotal ? formatMoney(lineTotal) : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 space-y-1 text-[11px] text-gray-700">
              <div className="flex items-center justify-between">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Discount {discountPercent ? `(${discountPercent}%)` : ''}</span>
                <span>-{formatMoney(discountAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Tax {taxPercent ? `(${taxPercent}%)` : ''}</span>
                <span>{formatMoney(taxAmount)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2 text-xs font-semibold text-gray-900">
                <span>Total</span>
                <span>{formatMoney(total)}</span>
              </div>
            </div>

            {note && (
              <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                <span className="font-medium text-gray-700">Note: </span>
                {note}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Invoices() {
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [showFromJobModal, setShowFromJobModal] = useState(false);

  // Use React Query for cached data
  const { data: allInvoices = [], isLoading: loading, error } = useInvoicesData();
  const [filteredInvoices, setFilteredInvoices] = useState<any[]>([]);
  
  // Pagination and filters state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  // Client-side filtering - no loading, instant results with cached data
  useEffect(() => {
    let filtered = [...allInvoices];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(invoice => 
        invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.job?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allInvoices, searchTerm]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Invoices</h1>
          <p className="page-subtitle">Loading...</p>
        </div>
        <div className="table-container">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="loading-skeleton skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Invoices</h1>
            <p className="page-subtitle">Manage invoices, track payments, and monitor cash flow.</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowFromJobModal(true)}
            >
              Create Invoice
            </button>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Invoices</h3>
          <p className="text-red-700 mb-4">{error?.message || 'Failed to load invoices'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate pagination for filtered results - ensure filteredInvoices is not empty
  const invoicesToShow = filteredInvoices.length > 0 ? filteredInvoices : allInvoices;
  const totalPages = Math.ceil(invoicesToShow.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = invoicesToShow.slice(startIndex, startIndex + itemsPerPage);

  if (allInvoices.length === 0 && !loading) {
    return (
      <div className="page-container">
        {mode === 'create' ? (
          <InvoiceComposer onCancel={() => setMode('list')} />
        ) : (
          <div className="py-6">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-normal text-[#515959]">Invoices</p>
                <h1 className="mt-1 text-2xl font-semibold text-[#191919] leading-tight">Invoices</h1>
              </div>
              <button
                type="button"
                onClick={() => setShowFromJobModal(true)}
                className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create invoice
              </button>
            </div>

            <EmptyState
              icon={DocumentTextIcon}
              title="No invoices yet"
              description="Create invoices for completed jobs to track payments."
              actionLabel="Create Invoice"
              onAction={() => setShowFromJobModal(true)}
            />
          </div>
        )}

        <CreateInvoiceFromJobModal
          open={showFromJobModal}
          onClose={() => setShowFromJobModal(false)}
        />
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div className="page-container">
        <InvoiceComposer onCancel={() => setMode('list')} />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="mb-6 border-b border-[#E6E6E4] pb-3">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Invoices</h1>
              <p className="mt-1 text-xs font-normal text-[#515959]">
                Manage invoices, track payments, and monitor cash flow across your jobs.
              </p>
            </div>

            <div className="flex flex-1 items-center justify-start gap-3 md:justify-end md:gap-4">
              {/* Header Search */}
              <div className="relative w-full max-w-xs">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search invoices..."
                  className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80"
                />
              </div>

              {/* Primary Action */}
              <button
                type="button"
                onClick={() => setShowFromJobModal(true)}
                className="inline-flex items-center justify-center rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-600 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create invoice
              </button>
            </div>
          </div>
        </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filter-group">
          <select className="filter-select">
            <option>All Invoices</option>
            <option>Paid</option>
            <option>Pending</option>
            <option>Overdue</option>
            <option>Sent</option>
          </select>
          
          <select className="filter-select">
            <option>This Month</option>
            <option>Last Month</option>
            <option>Last 3 Months</option>
            <option>This Year</option>
          </select>
          
          <select className="filter-select">
            <option>All Clients</option>
            <option>John Smith</option>
            <option>Sarah Wilson</option>
            <option>Robert Taylor</option>
          </select>
        </div>
        
        <div className="flex items-center gap-3 ml-auto">
          <button className="btn btn-secondary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="transactions-table">
            <thead className="table-header">
              <tr>
                <th className="col-checkbox">
                  <input type="checkbox" className="table-checkbox" />
                </th>
                <th className="col-description">Invoice Details</th>
                <th className="col-account">Client & Job</th>
                <th className="col-amount">Amount</th>
                <th className="col-date">Issue Date</th>
                <th className="col-payment">Due Date</th>
                <th className="col-actions">Status</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((invoice: any) => (
                <tr key={invoice.id} className="table-row">
                  <td className="col-checkbox">
                    <input type="checkbox" className="table-checkbox" />
                  </td>
                  <td className="col-description">
                    <div className="transaction-description">
                      <div className="transaction-icon-wrapper">
                        <DocumentTextIcon 
                          className="w-5 h-5"
                          style={{ color: '#6B7280' }}
                        />
                      </div>
                      <div className="transaction-details">
                        <h4>{invoice.invoiceNumber}</h4>
                        <p>Invoice for services</p>
                      </div>
                    </div>
                  </td>
                  <td className="col-account">
                    <div className="flex flex-col">
                      <div className="flex items-center mb-1">
                        <UserIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium">{invoice.client?.name || 'Unknown Client'}</span>
                      </div>
                      <div className="flex items-center">
                        <CalendarDaysIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">{invoice.job?.title || 'No job linked'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="col-amount">
                    <span className="text-sm font-semibold">£{(invoice.totalAmount || 0).toFixed(2)}</span>
                  </td>
                  <td className="col-date">
                    <span className="text-sm">{formatDate(invoice.createdAt)}</span>
                  </td>
                  <td className="col-payment">
                    <span className="text-sm">{formatDate(invoice.dueDate)}</span>
                  </td>
                  <td className="col-actions">
                    <span className={`status-badge ${invoice.status.toLowerCase()}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="col-actions">
                    <InvoiceActionsMenu invoice={invoice} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Page {totalPages === 0 ? 0 : currentPage} of {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              className="page-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button className="page-btn active">{currentPage}</button>
            {currentPage < totalPages && (
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <CreateInvoiceFromJobModal
        open={showFromJobModal}
        onClose={() => setShowFromJobModal(false)}
      />
    </div>
  );
}
